// Product Grid Management Module
const ProductGridManager = (() => {
  // プライベート定数
  const CONSTANTS = {
    SORT_BY: {
      SALE_OFF: 'sale_off',
      PUBLISHED_AT: 'published_at',
      PRICE_ASCENDING: 'price-ascending',
      BEST_SELLING: 'best-selling',
      CREATED_DESCENDING: 'created-descending',
      RELEVANCE: 'relevance',
      EXPIRATION: 'expiration-ascending',
    },
    FILTER_BY: {
      FREE_SHIPPING: 'free-shipping',
      FREE_COOL_FLIGHT: 'free-cool-flight',
      FREE_SHIPPING_AND_COOL_FLIGHT: 'free-shipping-and-free-cool-flight',
      UNDER_3000: 'under-3000',
      UNDER_2000: 'under-2000',
      UNDER_1000: 'under-1000',
    },
    SCROLL_POSITION_PREFIX: 'scrollPosition_',
    SELECTORS: {
      SORT_BUTTONS: '#SortBy-mobile, #SortBy',
      PRODUCT_PRICE: '.product_price',
      PRODUCT_OFF_RATIO: '.product-offratio',
      PRODUCT_PUBLISHED_DATE: '.product-published-date',
      PRODUCT_EXPIRATION_DATE: '.product-expiration-date',
      PRODUCT_FREE_SHIPPING: '.product-free-shipping',
      PRODUCT_FREE_COOL_FLIGHT: '.product-free-cool-flight',
      PRODUCT_FREE_SHIPPING_AND_COOL_FLIGHT: '.product-free-shipping-and-free-cool-flight',
    },
  };

  class ProductBase {
    constructor() {
      this.targetId = this.determineTargetId();
    }

    determineTargetId() {
      return window.location.pathname.includes('search') ? 'main-search-product-grid' : 'main-collection-product-grid';
    }

    updateProductGrid(sortedNodes) {
      const container = document.getElementById(this.targetId);
      if (!container) {
        return;
      }
      container.innerHTML = '';
      if (sortedNodes && sortedNodes.length > 0) {
        sortedNodes.forEach((node) => container.appendChild(node));
      } else {
        // 結果がない場合のフォールバック表示を追加
        const noResultMessage = document.createElement('div');
        noResultMessage.className = 'no-results-message';
        noResultMessage.textContent = '該当する商品がありません';
        container.appendChild(noResultMessage);
      }
    }

    storeOriginalNodes() {
      if (!this.originalNodes) {
        this.originalNodes = Array.from(document.querySelectorAll(`#${this.targetId} li`));
      }
      return this.originalNodes;
    }

    resetProductGrid(param) {
      if (this.originalNodes && this.originalNodes.length > 0) {
        const clonedNodes = this.originalNodes.map((node) => node.cloneNode(true));
        this.updateProductGrid(clonedNodes);
      } else {
        this.storeOriginalNodes();
        if (this.originalNodes && this.originalNodes.length > 0) {
          this.resetProductGrid();
        } else {
          const url = new URL(window.location.href);
          if (param) {
            url.searchParams.set('sort_by', param);
          } else {
            url.searchParams.delete('sort_by');
          }
          window.location.href = url.toString();
        }
      }
    }
  }

  class ProductSorter extends ProductBase {
    getProductId(node) {
      return parseInt(node.dataset.id, 10);
    }

    sortByPrice(productNodeA, productNodeB) {
      const priceA = parseInt(productNodeA.querySelector(CONSTANTS.SELECTORS.PRODUCT_PRICE).value, 10);
      const priceB = parseInt(productNodeB.querySelector(CONSTANTS.SELECTORS.PRODUCT_PRICE).value, 10);
      const priceDiff = priceA - priceB;
      return priceDiff || this.getProductId(productNodeB) - this.getProductId(productNodeA);
    }

    sortBySaleOff(productNodeA, productNodeB) {
      const discountRateA = productNodeA.querySelector(CONSTANTS.SELECTORS.PRODUCT_OFF_RATIO).value;
      const discountRateB = productNodeB.querySelector(CONSTANTS.SELECTORS.PRODUCT_OFF_RATIO).value;

      if (!discountRateB) return -1;
      if (!discountRateA) return 1;

      const discountRateDiff = parseFloat(discountRateB) - parseFloat(discountRateA);
      return discountRateDiff || this.getProductId(productNodeB) - this.getProductId(productNodeA);
    }

    sortByPublishedDate(productNodeA, productNodeB) {
      const publishedDateA = new Date(productNodeA.querySelector(CONSTANTS.SELECTORS.PRODUCT_PUBLISHED_DATE).value);
      const publishedDateB = new Date(productNodeB.querySelector(CONSTANTS.SELECTORS.PRODUCT_PUBLISHED_DATE).value);
      const dateDiff = publishedDateB - publishedDateA;
      return dateDiff || this.getProductId(productNodeB) - this.getProductId(productNodeA);
    }

    sortByExpiration(productNodeA, productNodeB) {
      const expirationDateAValue = productNodeA.querySelector(CONSTANTS.SELECTORS.PRODUCT_EXPIRATION_DATE).value;
      const expirationDateBValue = productNodeB.querySelector(CONSTANTS.SELECTORS.PRODUCT_EXPIRATION_DATE).value;

      if (!expirationDateAValue) return 1;
      if (!expirationDateBValue) return -1;

      const expirationDateA = new Date(expirationDateAValue);
      const expirationDateB = new Date(expirationDateBValue);
      const dateDiff = expirationDateA - expirationDateB;
      return dateDiff || this.getProductId(productNodeB) - this.getProductId(productNodeA);
    }

    getSortedNodes(sortBy) {
      if (
        sortBy === CONSTANTS.SORT_BY.SALE_OFF ||
        sortBy === CONSTANTS.SORT_BY.EXPIRATION ||
        sortBy === CONSTANTS.SORT_BY.PUBLISHED_AT
      ) {
        this.resetProductGrid(sortBy);
      }

      const list = Array.from(document.querySelectorAll(`#${this.targetId} li`));
      const sortingStrategies = {
        [CONSTANTS.SORT_BY.PRICE_ASCENDING]: (a, b) => this.sortByPrice(a, b),
        [CONSTANTS.SORT_BY.SALE_OFF]: (a, b) => this.sortBySaleOff(a, b),
        [CONSTANTS.SORT_BY.PUBLISHED_AT]: (a, b) => this.sortByPublishedDate(a, b),
        [CONSTANTS.SORT_BY.EXPIRATION]: (a, b) => this.sortByExpiration(a, b),
      };

      return sortingStrategies[sortBy] ? list.sort(sortingStrategies[sortBy]) : list;
    }

    handleURLBasedSort(sortBy, isSearchPage) {
        if (isSearchPage) {
            // 検索ページでも現在のURLパラメータを維持しつつ sort_by のみ更新
            const currentUrl = new URL(window.location.href);
            // 検索ロジックに必要な options[prefix]=last がなければ付与
            if (!currentUrl.searchParams.has('options[prefix]')) {
                currentUrl.searchParams.set('options[prefix]', 'last');
            }
            currentUrl.searchParams.set('sort_by', sortBy);
            return currentUrl.pathname + currentUrl.search;
        }

        // 現在のURLパラメータを維持
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('sort_by', sortBy);
        currentUrl.searchParams.set('sort_by', sortBy);
        // ソート変更時にはページ番号をリセットして1ページ目に戻る
        currentUrl.searchParams.delete('page');
        return currentUrl.pathname + currentUrl.search;
    }
  }

  class ProductFilter extends ProductBase {
    filterByFreeShipping(node) {
      const element = node.querySelector(`input${CONSTANTS.SELECTORS.PRODUCT_FREE_SHIPPING}`);
      return element && element.value === 'true';
    }

    filterByFreeCoolFlight(node) {
      const element = node.querySelector(`input${CONSTANTS.SELECTORS.PRODUCT_FREE_COOL_FLIGHT}`);
      return element && element.value === 'true';
    }

    filterByFreeShippingAndCoolFlight(node) {
      const element = node.querySelector(`input${CONSTANTS.SELECTORS.PRODUCT_FREE_SHIPPING_AND_COOL_FLIGHT}`);
      return element && element.value === 'true';
    }

    filterByPrice(node, price) {
      const element = node.querySelector('.product-list-item__price-sale');
      if (!element) return false;
      const priceText = element.innerHTML;
      const productPrice = parseInt(priceText.replace(/[^0-9]/g, ''), 10);
      return !Number.isNaN(productPrice) && productPrice <= price;
    }

    getFilteredNodes(filterBy) {
      this.resetProductGrid(filterBy);
      const list = Array.from(document.querySelectorAll(`#${this.targetId} li`));
      const filteringStrategies = {
        [CONSTANTS.FILTER_BY.FREE_SHIPPING]: (node) => this.filterByFreeShipping(node),
        [CONSTANTS.FILTER_BY.FREE_COOL_FLIGHT]: (node) => this.filterByFreeCoolFlight(node),
        [CONSTANTS.FILTER_BY.FREE_SHIPPING_AND_COOL_FLIGHT]: (node) => this.filterByFreeShippingAndCoolFlight(node),
        [CONSTANTS.FILTER_BY.UNDER_3000]: (node) => this.filterByPrice(node, 3000),
        [CONSTANTS.FILTER_BY.UNDER_2000]: (node) => this.filterByPrice(node, 2000),
        [CONSTANTS.FILTER_BY.UNDER_1000]: (node) => this.filterByPrice(node, 1000),
      };

      return filteringStrategies[filterBy] ? list.filter(filteringStrategies[filterBy]) : list;
    }
  }

  class ScrollPositionManager {
    constructor() {
      this.prefix = CONSTANTS.SCROLL_POSITION_PREFIX;
    }

    getStorageKey() {
      return `${this.prefix}${window.location.href}`;
    }

    savePosition() {
      const sessionStorageKey = this.getStorageKey();
      const scrollValueItem = sessionStorage.getItem(sessionStorageKey);
      if (!scrollValueItem) {
        sessionStorage.setItem(sessionStorageKey, window.scrollY);
      }
    }

    restorePosition() {
      const sessionStorageKey = this.getStorageKey();
      const scrollPosition = sessionStorage.getItem(sessionStorageKey);
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition, 10));
        sessionStorage.removeItem(sessionStorageKey);
      }
    }
  }

  // 初期化処理を行うパブリックメソッド
  const initialize = () => {
    const productSorter = new ProductSorter();
    const productFilter = new ProductFilter();
    const scrollManager = new ScrollPositionManager();

    // ソート選択のイベントリスナー設定
    document.querySelectorAll(CONSTANTS.SELECTORS.SORT_BUTTONS).forEach((element) => {
        element.addEventListener('change', (event) => {
            const sortBy = event.target.value;
            const isSearchPage = window.location.pathname.includes('search');

            // すべてのソートをURLベースで統一
            window.location.href = productSorter.handleURLBasedSort(sortBy, isSearchPage);
        });
    });

    // URL パラメータに基づく初期ソート
    const sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if ([CONSTANTS.SORT_BY.SALE_OFF, CONSTANTS.SORT_BY.PUBLISHED_AT, CONSTANTS.SORT_BY.EXPIRATION, CONSTANTS.SORT_BY.PRICE_ASCENDING].includes(sortBy)) {
      const sortByElement = document.getElementById('SortBy');
      if (sortByElement) {
        sortByElement.value = sortBy;
        const sortedNodes = productSorter.getSortedNodes(sortBy);
        productSorter.updateProductGrid(sortedNodes);
      }
    } else if (
      [
        CONSTANTS.FILTER_BY.FREE_SHIPPING,
        CONSTANTS.FILTER_BY.FREE_COOL_FLIGHT,
        CONSTANTS.FILTER_BY.FREE_SHIPPING_AND_COOL_FLIGHT,
        CONSTANTS.FILTER_BY.UNDER_3000,
        CONSTANTS.FILTER_BY.UNDER_2000,
        CONSTANTS.FILTER_BY.UNDER_1000,
      ].includes(sortBy)
    ) {
      const filterByElement = document.getElementById('SortBy');
      if (filterByElement) {
        filterByElement.value = sortBy;
        const filteredNodes = productFilter.getFilteredNodes(sortBy); // 変数名の誤字を修正
        productFilter.updateProductGrid(filteredNodes);
      }
    }

    // スクロール位置の管理
    window.addEventListener('pagehide', () => scrollManager.savePosition());
    window.addEventListener('pageshow', () => scrollManager.restorePosition());

    // Yappliイベントの送信
    try {
      sendYappliEvent('search', {
        search_string: new URL(document.location).searchParams.get('q'),
      });
    } catch (error) {
      console.error('Failed to send Yappli event:', error); // eslint-disable-line no-console
    }
  };

  // パブリックAPI
  return {
    initialize,
  };
})();

// アプリケーションの初期化
(() => {
  ProductGridManager.initialize();
})();